/* eslint-disable @typescript-eslint/no-explicit-any */
import { Instant } from 'js-joda';
import _ from 'lodash';

import * as Dashboard from 'types';
import AppConstants from 'constants/AppConstants';
import AppSettings from 'settings/AppSettings';
import ClowncarAppConstants from 'constants/ClowncarAppConstants';
import protos from 'utils/protos';

const { BusinessOnboardingCreateRequest } = protos;

export function fetchOnboardingSalesforceDataByToken(token: string): ApiAction {
  return {
    TockApi: {
      getProto: `/onboarding/salesforce?token=${token}`,
      type: ClowncarAppConstants.ONBOARDING_SALESFORCE_GET_BUSINESS,
    },
  };
}

export function createOnboardingFromSalesforceAccount(accountId: string): ApiAction {
  return {
    TockApi: {
      getProto: `/onboarding/salesforce/create?accountId=${accountId}`,
      type: ClowncarAppConstants.ONBOARDING_SALESFORCE_CREATE_BUSINESS,
    },
  };
}

export function getBusinessFromSalesforceAccount(accountId: string): ApiAction {
  return {
    TockApi: {
      getProto: `/onboarding/salesforce/get?accountId=${accountId}`,
      type: ClowncarAppConstants.ONBOARDING_SALESFORCE_GET_BUSINESS,
      onError: _.noop,
    },
  };
}

export function createBusiness(
  onboardingToken: string,
  salesforceAccountId: string | undefined,
  business: Partial<Dashboard.core.Business>,
  hour: Dashboard.core.PublicHours[]
) {
  const request = BusinessOnboardingCreateRequest.fromObject({
    onboardingToken,
    salesforceAccountId,
    business: createBusinessObject(business),
    hour,
  });

  return {
    TockApi: {
      postProto: '/onboarding/create',
      type: ClowncarAppConstants.ONBOARDING_CREATE,
      request,
      toast: 'Business has been created!',
    },
  };
}

// Selectively copy over fields to give us more control over types
function createBusinessObject(
  business: Partial<Dashboard.core.Business>
): Partial<Dashboard.core.Business> {
  return {
    address: business.address,
    address2: business.address2,
    businessGroupId: business.businessGroupId || undefined,
    businessGroupName: business.businessGroupName,
    city: business.city,
    country: business.country,
    currencyCode: business.currencyCode,
    domainName: business.domainName,
    email: business.email,
    locale: business.locale,
    name: business.name,
    phone: business.phone,
    phoneExtension: business.phoneExtension,
    state: business.state,
    timeZone: business.timeZone,
    zipCode: business.zipCode,
    webUrl: business.webUrl,
    googlePlacesId: business.googlePlacesId,
    customerFacingPhone: business.phone,
    menuUrl: business.menuUrl,
    facebookUrl: business.facebookUrl,
    instagramId: business.instagramId,
    acceptsWalkins: business.acceptsWalkins,
    michelinStars: business.michelinStars,
    cuisines: business.cuisines,
    priceRange: business.priceRange,
    twitterHandle: business.twitterHandle,
    foursquareVenueId: business.foursquareVenueId,
    addressLat: business.addressLat,
    addressLng: business.addressLng,
    inventoryMode: business.inventoryMode,
    scheduledInventoryConfig: business.scheduledInventoryConfig,
  };
}

export function fetchTimeZone(lat: any, lng: any) {
  return (dispatch: Dispatch) => {
    const apiKey = AppSettings.GOOGLE_API_KEY;
    const timestamp = Instant.now().toEpochMilli() / 1000;
    const requestUrl =
      'https://maps.googleapis.com/maps/api/timezone/json' +
      `?location=${lat},${lng}&timestamp=${timestamp}&key=${apiKey}`;
    return fetch(requestUrl)
      .then((resp: any) => resp.json())
      .then((resp: any) => {
        if (resp.timeZoneId) {
          dispatch({
            type: AppConstants.ONBOARDING_FETCH_TIME_ZONE_DONE,
            payload: resp,
          });
          return resp.timeZoneId;
        }
      });
  };
}

export function setTimeZone(timeZoneId: string) {
  return {
    type: AppConstants.ONBOARDING_FETCH_TIME_ZONE_DONE,
    payload: timeZoneId,
  };
}

export function isDomainAvailable(domainName: any): ApiAction {
  return {
    TockApi: {
      getProto: `/business/domain?domain=${encodeURIComponent(domainName)}`,
      type: AppConstants.ONBOARDING_DOMAIN_CHECK,
    },
  };
}
